import React from "react";
import { useAuth } from "store/AuthProvider";
import { usePendingQueue } from "hooks/usePendingQueue";
import {
    Dialog,
    Paper,
    Button,
    Box,
    Typography,
    Alert,
    AlertTitle,
} from "@mui/material";
import { Warning, Block } from "@mui/icons-material";

/**
 * C: 登出与数据保护 - 登出模态框
 * 
 * C1: 正常登出 - 在线且无待处理数据时允许登出
 * C2: 强制拦截登出 - 有待处理数据时必须拦截，无任何旁路选项
 * C3: 离线登出 - 离线且数据已同步时允许登出
 */
const LogoutTipModal = ({
    open,
    isOnline,
    onClose,
    handleLogout,
}: {
    open: boolean;
    isOnline: boolean;
    onClose: () => void;
    handleLogout: () => void;
}) => {
    const auth = useAuth();
    const { getPendingDataInfo } = usePendingQueue();
    const [pendingCount, setPendingCount] = React.useState(0);
    const [failedCount, setFailedCount] = React.useState(0);
    const [isBlocked, setIsBlocked] = React.useState(false);

    React.useEffect(() => {
        if (open) {
            // Check for pending data when modal opens
            getPendingDataInfo().then(info => {
                setPendingCount(info.pendingCount);
                setFailedCount(info.failedCount);
                
                // C2: Block logout if there's any pending or failed data
                const hasPendingData = info.pendingCount > 0 || info.failedCount > 0;
                setIsBlocked(hasPendingData);
            });
        }
    }, [open, getPendingDataInfo]);

    // C2: 强制拦截 - 有待处理数据时不允许登出
    if (isBlocked) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '12px',
                        maxWidth: '400px',
                    },
                }}
            >
                <Paper sx={{ display: 'flex', flexDirection: 'column', gap: '16px', p: '24px' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Block color="error" />
                        <Typography variant="h6" fontWeight={600} color="error">
                            無法登出
                        </Typography>
                    </Box>

                    <Alert severity="error" sx={{ mb: 1 }}>
                        <AlertTitle sx={{ fontWeight: 600 }}>未同步的數據</AlertTitle>
                        <Typography variant="body2">
                            您有未同步的數據，必須先完成同步才能登出。
                        </Typography>
                    </Alert>

                    <Box
                        sx={{
                            p: 2,
                            backgroundColor: 'rgba(211, 47, 47, 0.08)',
                            borderRadius: '8px',
                            border: '1px solid rgba(211, 47, 47, 0.3)',
                        }}
                    >
                        {pendingCount > 0 && (
                            <Typography variant="body2" gutterBottom>
                                待同步數據: <strong>{pendingCount}</strong> 條
                            </Typography>
                        )}
                        {failedCount > 0 && (
                            <Typography variant="body2" gutterBottom>
                                同步失敗: <strong>{failedCount}</strong> 條
                            </Typography>
                        )}
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        請執行以下操作之一：
                    </Typography>

                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        <Typography component="li" variant="body2" color="text.secondary">
                            連接網絡並等待自動同步完成
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            前往旗袋包列表手動觸發同步
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            解決所有同步失敗的項目
                        </Typography>
                    </Box>

                    <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
                        注意：為保護您的數據，系統不提供強制登出或跳過選項。
                    </Typography>

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={onClose}
                        sx={{
                            backgroundColor: '#269FFF',
                            color: '#fff',
                            borderRadius: '6px',
                            py: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            '&:hover': {
                                backgroundColor: '#1976d2',
                            },
                        }}
                    >
                        確定
                    </Button>
                </Paper>
            </Dialog>
        );
    }

    // C1 & C3: 允许登出 - 无待处理数据
    return (
        <Dialog
            open={open}
            onClose={onClose}
            sx={{
                '& .MuiDialog-paper': {
                    width: '300px',
                    borderRadius: '12px',
                },
            }}
        >
            <Paper sx={{ display: 'flex', flexDirection: 'column', gap: '16px', p: '18px' }}>
                <Box sx={{ fontSize: '14px', fontWeight: '500', fontStyle: 'normal', lineHeight: 'normal', color: '#000' }}>
                    登出
                </Box>
                <Box sx={{ fontSize: '14px', fontWeight: '400', fontStyle: 'normal', lineHeight: 'normal', color: '#000' }}>
                    {isOnline ? (
                        // C1: 在线正常登出
                        `${auth.getUserEmailValue() || auth.getUserNameValue()} 的帳戶?`
                    ) : (
                        // C3: 离线登出（数据已同步）
                        <>
                            在沒有互聯網連線下登出？
                            <Box component="span" sx={{ display: 'block', mt: 1, color: 'success.main', fontSize: '12px' }}>
                                ✓ 所有數據已同步
                            </Box>
                        </>
                    )}
                </Box>
                <Button
                    sx={{
                        backgroundColor: '#269FFF',
                        color: '#fff',
                        borderRadius: '6px',
                        py: '6px',
                        fontSize: '14px',
                        fontWeight: '400',
                        '&:hover': {
                            backgroundColor: '#1976d2',
                        },
                    }}
                    onClick={handleLogout}
                >
                    確認登出
                </Button>
            </Paper>
        </Dialog>
    );
};

export default LogoutTipModal;